import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { addAnalysis } from '../../lib/analysisStore';
import { auth } from '../../lib/firebase';
import { RatingSliders } from './RatingSliders';
import { BreadParametersForm } from './BreadParametersForm';
import { AnalysisChart } from './AnalysisChart';
import { FeedbackSection } from './FeedbackSection';
import { ANALYSIS_RATINGS } from '../../types/analysis';
import { useSubscription } from '../subscription/SubscriptionContext';

interface AnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string;
  onKnowledgeClick?: () => void;
}

const initialAnalysisData = {
  comments: '',
  ratings: {
    crumbStructure: 50,
    ovenSpring: 50,
    crust: 50,
    crumbTexture: 50
  },
  parameters: {
    fermentationType: 'direct',
    fermentationDuration: 6,
    bakingMethod: 'dutch-oven',
    temperature: 230,
    weight: 1,
    bakingDuration: 45,
    hydration: 65 // Default hydration
  },
  feedback: {
    mainIssue: '',
    methodology: '',
    improvements: [] as string[]
  }
};

export function AnalysisModal({ isOpen, onClose, sessionId, onKnowledgeClick }: AnalysisModalProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState(initialAnalysisData);
  const [analyzing, setAnalyzing] = useState(false);
  const { isSubscribed } = useSubscription();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setLoading(false);
      setError(null);
      setSelectedImage(null);
      setImagePreview(null);
      setAnalysisData(initialAnalysisData);
      setAnalyzing(false);
    }
  }, [isOpen]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleNext = () => {
    if (step === 3) {
      // Show loading state before moving to step 4
      setAnalyzing(true);
      setTimeout(() => {
        setAnalyzing(false);
        setStep(4);
      }, 3000);
    } else if (step < 4) {
      setStep(step + 1);
    } else {
      const formattedData = {
        ...analysisData,
        targetBakeTime: analysisData.targetBakeTime ? new Date(analysisData.targetBakeTime) : new Date(),
        starterSchedule: {
          ...analysisData.starterSchedule,
          feeds: analysisData.starterSchedule?.feeds.map(feed => ({
            ...feed,
            scheduledTime: new Date(feed.scheduledTime)
          })) || []
        }
      };
      onSubmit(formattedData);
      onClose();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return analysisData.comments.trim().length > 0;
      case 2:
        return true; // Ratings always have default values
      case 3:
        return true; // Parameters always have default values
      case 4:
        return true; // Feedback is generated automatically
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!auth.currentUser?.uid) {
      setError('You must be logged in to create an analysis');
      return;
    }

    // Check subscription status before attempting to save
    if (!isSubscribed) {
      setError('You need an active subscription to create analyses. Please subscribe or start a free trial to continue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await addAnalysis({
        userId: auth.currentUser.uid,
        sessionId,
        ...analysisData
      }, selectedImage);

      onClose();
    } catch (err: any) {
      console.error('Error creating analysis:', err);

      // Check if it's a permission error
      if (err?.code === 'permission-denied' || err?.message?.includes('permission')) {
        setError('You need an active subscription to create analyses. Please check your subscription status or start a free trial.');
      } else {
        setError('Failed to create analysis. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl text-white font-brand font-light italic">New Bread Analysis</h2>
              <p className="text-sm text-white/70 mt-1">{getStepTitle()}</p>
            </div>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="mt-4 flex gap-2">
            {[1, 2, 3, 4].map((stepNumber) => (
              <div
                key={stepNumber}
                className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                  step >= stepNumber ? 'bg-amber-400' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
              {error}
            </div>
          )}

          {analyzing ? (
            <div className="min-h-[400px] flex flex-col items-center justify-center space-y-6">
              <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
              <p className="text-white text-center max-w-sm">
                We are analyzing your bread and preparing some personalized feedback, just for you!
              </p>
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex justify-between">
            {step > 1 ? (
              <button
                onClick={handleBack}
                className="px-6 py-2 text-white border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
              >
                Back
              </button>
            ) : (
              <div></div>
            )}
            <button
              onClick={step === 4 ? handleSubmit : handleNext}
              disabled={!isStepValid() || loading || analyzing}
              className={`px-6 py-2 rounded-xl transition-all duration-300 ${
                isStepValid() && !loading && !analyzing
                  ? 'bg-gradient-to-r from-amber-500/90 to-amber-600/90 text-white hover:from-amber-500 hover:to-amber-600'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : step === 4 ? (
                'Save Analysis'
              ) : step === 3 ? (
                'Analyse Now'
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  function getStepTitle() {
    switch (step) {
      case 1:
        return 'Basic Information';
      case 2:
        return 'Rate Your Bread';
      case 3:
        return 'Baking Parameters';
      case 4:
        return 'Analysis Results';
      default:
        return '';
    }
  }

  function renderStepContent() {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Upload Bread Photo (Optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                id="bread-photo"
              />
              
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Selected preview"
                    className="w-full aspect-video object-cover rounded-xl"
                  />
                  <button
                    onClick={() => {
                      setSelectedImage(null);
                      setImagePreview(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 rounded-full text-white/90 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="bread-photo"
                  className="block w-full aspect-video border-2 border-dashed border-white/20 rounded-xl hover:border-amber-400/50 hover:bg-white/5 transition-colors cursor-pointer"
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <span className="text-white/70">Click to upload your bread photo</span>
                  </div>
                </label>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Comments
              </label>
              <textarea
                value={analysisData.comments}
                onChange={(e) => setAnalysisData({ ...analysisData, comments: e.target.value })}
                placeholder="Share your observations about this bread..."
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 min-h-[100px]"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <RatingSliders
            ratings={analysisData.ratings}
            onChange={(ratings) => setAnalysisData({ ...analysisData, ratings })}
          />
        );

      case 3:
        return (
          <div className="space-y-8">
            <BreadParametersForm
              parameters={analysisData.parameters}
              onChange={(parameters) => setAnalysisData({ ...analysisData, parameters })}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <h3 className="font-medium text-white">A summary of your ratings</h3>
            <div className="flex justify-center">
              <AnalysisChart ratings={analysisData.ratings} />
            </div>
            <div className="p-4 bg-white/5 rounded-xl space-y-6">
              <h3 className="text-xl text-white font-brand font-light italic">Analysis Results</h3>
              <FeedbackSection
                ratings={analysisData.ratings}
                parameters={analysisData.parameters}
                onFeedbackGenerated={(feedback) => {
                  setAnalysisData(prev => ({
                    ...prev,
                    feedback
                  }));
                }}
                onKnowledgeClick={onKnowledgeClick}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  }
}